import { Component, ElementRef, ViewChild, InputSignal, input, AfterViewInit, WritableSignal, signal, computed, QueryList, ViewChildren, Signal, DestroyRef, ChangeDetectorRef, effect, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SHARED_MODULES } from '../../../shared.';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { ChatService, FILE_EXTENSIONS_ALLOWED } from '../../services/chat.service';
import { IChatMessage, IChatTempMessage, IMentionableUsers, INewChatMessage } from '../../models/chat';
import { UserLogoGenerateComponent } from '../user-logo-generate/user-logo-generate.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { SnackBarService } from '../../services/snack-bar.service';
import { LoaderComponent } from '../loader/loader.component';
import { IImagesViewModalData, ImagesViewModalComponent } from '../../modals/images-view-modal/images-view-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { MixpanelService } from '../../services/mixpanel.service';
import { MixpanelEvents } from '../../models/mixpanelEvents';
import { MentionParserPipe } from '../../pipes/mention-parser.pipe';
import { Utils } from '../../services/utils.service';

export type AttachmentFile = {
    file: File;
    loading: boolean;
    key?: string;
    link?: string;
    subscription?: Subscription;
    previewUrl?: string;
    icon: string;
}

export type StoreItem = {
    id: number;
    name: string;
    users: Array<IMentionableUsers>;
}

type ChatFormGroup = {
	message: FormControl<string>;
    files?: FormControl<Array<AttachmentFile>>;
}

const FILE_SIZE_LIMIT: number = 100 * 1024 * 1024;
const FILES_COUNT_LIMIT: number = 20;

@Component({
    selector: 'ph-chat-input',
    templateUrl: './ph-chat-input.component.html',
    styleUrls: ['./ph-chat-input.component.scss'],
    imports: [...SHARED_MODULES, PhIconWrapperComponent, UserLogoGenerateComponent, PhIconComponent, LoaderComponent, MentionParserPipe]
})
export class PhChatInputComponent implements OnInit, AfterViewInit {

    private formValueSignal: Signal<{ message: string, files?: Array<AttachmentFile> }>;

    public allowAttachments: InputSignal<boolean> = input<boolean>(true);
    public allowMentions: InputSignal<boolean> = input<boolean>(true);
    public maxLength: InputSignal<number> = input<number>(2000);
    public placeholderLabel: InputSignal<string> = input<string>('CHAT.WRITE_MESSAGE');

    public formGroup: FormGroup<ChatFormGroup>;
    public joinedAllowedExtensions: WritableSignal<string> = signal<string>('');
    public isDragging: WritableSignal<boolean> = signal<boolean>(false);
    public mentionedUsersIds: Set<number> = new Set<number>();
    public mentionQuery: WritableSignal<string> = signal<string>('');
    public repliedMessage: WritableSignal<IChatMessage> = signal<IChatMessage>(null);
    public selectedMentionIndex: WritableSignal<number> = signal<number>(-1);
    public showUserPicker: WritableSignal<boolean> = signal<boolean>(false);
    public selectedTabIndex: WritableSignal<number> = signal<number>(0);
    public expandedStoreId: WritableSignal<number | null> = signal<number | null>(null);
 
    @ViewChild('textarea') textareaElement!: ElementRef<HTMLTextAreaElement>;
    @ViewChildren('pickerContainer') pickerContainers!: QueryList<ElementRef<HTMLDivElement>>;
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
    @ViewChildren('userItem') userItems!: QueryList<ElementRef<HTMLDivElement>>;
    @ViewChildren('storeItem') storeItems!: QueryList<ElementRef<HTMLDivElement>>;

    public allStores: Signal<Array<StoreItem>> = computed(() => {
        if (!this.allowMentions()) return [];
        
        const storesMap = new Map<number, StoreItem>();
        for (let user of this.chatService.mentionableUsers()) {
            for (let plan of user.plans) {
                if (!storesMap.has(plan.id)) {
                    storesMap.set(plan.id, { ...plan, users: [user] });
                } else {
                    storesMap.get(plan.id)!.users.push(user);
                }
            }
        }

        return Array.from(storesMap.values());
    });

    public filteredUsers: Signal<Array<IMentionableUsers>> = computed(() => {
        if (!this.allowMentions()) return [];
        const query: string = this.mentionQuery().toLowerCase();
        const users: Array<IMentionableUsers> = this.chatService.mentionableUsers();
        if (!query) return users;
        return users.filter((user: IMentionableUsers) => user.full_name.toLowerCase().includes(query));
    });
    
    public filteredStores: Signal<Array<StoreItem>> = computed(() => {
        const query: string = this.mentionQuery().toLowerCase();
        const stores = this.allStores();
        if (!query) return stores;
        return stores.filter((store: StoreItem) => store.name.toLowerCase().includes(query));
    });

    public isSendDisabled: Signal<boolean> = computed(() => {
        const { message, files } = this.formValueSignal();
        const content = message?.trim() || '';
        
        if (!this.allowAttachments()) return content.length === 0;
        
        const hasFiles = files?.length > 0;
        const isUploading = files?.some(f => f.loading);
        return (!content && !hasFiles) || isUploading;
    });

    constructor(
        private dialog: MatDialog,
		private formBuilder: FormBuilder,
		private chatService: ChatService,
        private destroyRef: DestroyRef,
        private snackBarService: SnackBarService,
        private mixpanelService: MixpanelService
	) {
        this.formGroup = this.formBuilder.group<ChatFormGroup>({
            message: this.formBuilder.control<string>('', [
                Validators.required,
                Validators.maxLength(this.maxLength()),
                (control: FormControl<string>) => {
                    const value = control.value?.trim() || '';
                    return value.length === 0 ? { empty: true } : null;
                }
            ])
        });

        if (this.allowAttachments()) {
            this.formGroup.addControl('files', this.formBuilder.control<Array<AttachmentFile>>([]));
        }

        this.formValueSignal = toSignal(
            this.formGroup.valueChanges.pipe(
                map(value => {
                    const result: IChatTempMessage = { message: value.message ?? '' };
                    if (this.allowAttachments()) result.files = value.files || [];
                    return result;
                })
            ), 
            { initialValue: { message: '', files: [] } }
        );

        effect(() => {
            const value: IChatTempMessage = this.formValueSignal();
            this.chatService.tempMessage = value;
        });

        if (this.chatService.tempMessage) {
            this.formGroup.controls.message.setValue(this.chatService.tempMessage.message);
            if (this.formGroup.get('files')) this.formGroup.controls.files.setValue(this.chatService.tempMessage.files);
        }
    }

    ngOnInit(): void {
        this.joinedAllowedExtensions.set(FILE_EXTENSIONS_ALLOWED.map(ext => `.${ext}`).join(','));
        
        this.chatService.replyMessage$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((message: IChatMessage) => {
                this.repliedMessage.set(message);
                
                const user = this.chatService.mentionableUsers().find(u => u.id === message.user?.id);
                if (user) {
                    const currentContent = this.formGroup.controls.message.value || '';
                    const mention = `@${user.full_name.trim()} `;
                    this.formGroup.controls.message.setValue(mention + currentContent);
                    this.mentionedUsersIds.add(user.id);
                }

                this.textareaElement.nativeElement.focus();
                this.setTextareaHeight();
            });
    }

    ngAfterViewInit(): void {
		this.setTextareaHeight();
		this.textareaElement.nativeElement.focus();

        this.destroyRef.onDestroy(() => {
            const files = this.chatService.tempMessage?.files;
            if (files?.length) {
                this.chatService.tempMessage.files = files.filter((file: AttachmentFile) => !file.loading);
            }
        });
	}

    private isImage(file: File): boolean {
        return file.type.startsWith('image/');
    }

    private isVideo(file: File): boolean {
        return file.type.startsWith('video/');
    }


    private findMentionStartIndex(): number {
        const value: string = this.formGroup.controls.message.value || '';
        const cursorPosition: number = this.textareaElement.nativeElement.selectionStart || 0;
        const textBeforeCursor: string = value.substring(0, cursorPosition);
        
        const lastAtIndex: number = textBeforeCursor.lastIndexOf('@');
        if (lastAtIndex === -1) return -1;
        const textAfterAt: string = textBeforeCursor.substring(lastAtIndex + 1);
        if (textAfterAt.endsWith(' ')) return -1;
        const spaceCount: number = (textAfterAt.match(/ /g) || []).length;
        if (spaceCount > 1) return -1;
        if (textAfterAt.includes('\n')) return -1;

        return lastAtIndex;
    }

    private processFiles(files: FileList | Array<File>): void {
        if (!files || !this.allowAttachments()) return;
        
        const overSizeFileNames: Array<string> = [];
        const notAllowedExtensionFileNames: Array<string> = [];

        let allowedFiles: Array<File> = Array.from(files).filter((file: File) => {
            const extension: string = file.name.split('.').pop()?.toLowerCase();
            const isAllowedExtension: boolean = FILE_EXTENSIONS_ALLOWED.includes(extension);
            const isAllowedSize: boolean = file.size <= FILE_SIZE_LIMIT;

            if (!isAllowedExtension) notAllowedExtensionFileNames.push(file.name);
            if (!isAllowedSize) overSizeFileNames.push(file.name);
            return isAllowedExtension && isAllowedSize;
        });

        if (overSizeFileNames.length) {
            this.snackBarService.setSnackBarNotification({
                messageText: 'CHAT.FILES_OVER_SIZE',
                translateParams: { files: overSizeFileNames.join(', ') },
            });
        }
        if (notAllowedExtensionFileNames.length) {
            this.snackBarService.setSnackBarNotification({
                messageText: 'CHAT.FILES_NOT_ALLOWED_EXTENSION',
                translateParams: { files: notAllowedExtensionFileNames.join(', ') },
            });
        }

        const currentFilesCount = this.formGroup.controls.files?.value?.length || 0;
        if (currentFilesCount + allowedFiles.length > FILES_COUNT_LIMIT) {
            this.snackBarService.setSnackBarNotification({
                messageText: 'CHAT.FILES_COUNT_LIMIT_EXCEEDED',
            });
            allowedFiles = allowedFiles.slice(0, FILES_COUNT_LIMIT - currentFilesCount);
        }

        if (allowedFiles.length === 0) return;

        allowedFiles.forEach(async (file: File) => {
            let extension: string = file.name.split('.').pop()?.toLowerCase() || '';
            const fileItem: AttachmentFile = { file, loading: true, icon: this.chatService.getIconForFileExtension(extension) };
            
            const currentFiles = this.formGroup.controls.files?.value || [];
            this.formGroup.controls.files?.setValue([...currentFiles, fileItem]);

            fileItem.previewUrl = 'loading';
            let fileToUpload = file;
            if (extension === 'heic') {
                fileToUpload = await Utils.heicToJpeg(file);
                fileItem.file = fileToUpload;
                extension = fileToUpload.name.split('.').pop().toLowerCase();
            }

            if (this.isImage(fileToUpload)) {
                fileToUpload = await Utils.resizeImage(fileToUpload);
                fileItem.file = fileToUpload;
                fileItem.previewUrl = URL.createObjectURL(fileToUpload);
            } else if (this.isVideo(fileToUpload)) {
                fileItem.previewUrl = URL.createObjectURL(fileToUpload);
            }

            fileItem.subscription = this.chatService.uploadFile(fileToUpload)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (response: { key: string, file: string }) => {
                        const updated: Array<AttachmentFile> = [...(this.formGroup.controls.files?.value || [])];
                        const index = updated.indexOf(fileItem);
                        if (index !== -1) {
                            updated[index] = { ...fileItem, loading: false, key: response.key, link: response.file };
                        }
                        this.formGroup.controls.files?.setValue(updated);
                    },
                    error: () => {
                        const updated = [...(this.formGroup.controls.files?.value || [])];
                        const index = updated.indexOf(fileItem);
                        if (index !== -1) {
                            updated.splice(index, 1);
                        }
                        this.formGroup.controls.files?.setValue(updated);
                        this.snackBarService.setSnackBarNotification({
                            messageText: 'CHAT.FAILED_FILE_ATTACH',
                            translateParams: { file_name: file.name }
                        });
                    }
                });
        });

        this.setTextareaHeight();
    }

    private scrollSelectedIntoView(): void {
        setTimeout(() => {
            const container: HTMLDivElement = this.pickerContainers.toArray()[this.selectedTabIndex()]?.nativeElement;
            const items: QueryList<ElementRef<HTMLDivElement>> = this.selectedTabIndex() === 0 ? this.userItems : this.storeItems;
            const selectedItem: HTMLDivElement = items.toArray()[this.selectedMentionIndex()]?.nativeElement;

            if (container && selectedItem) {
                const offset: number = 4;
                const containerTop: number = container.scrollTop;
                const containerBottom: number = containerTop + container.clientHeight;
                const itemTop: number = selectedItem.offsetTop - offset;
                const itemBottom: number = selectedItem.offsetTop + selectedItem.clientHeight + offset;

                if (itemTop < containerTop) {
                    container.scrollTop = itemTop;
                } else if (itemBottom > containerBottom) {
                    container.scrollTop = itemBottom - container.clientHeight;
                }
            }
        });
    }

    private setTextareaHeight(): void {
        this.chatService.inputMessage$.next();
		const textarea: HTMLTextAreaElement = this.textareaElement.nativeElement;
		textarea.style.height = 'auto';
		textarea.style.height = `${textarea.scrollHeight}px`;
	}

    protected addMentionSymbol(): void {
        if (!this.allowMentions()) return;
        
        const textarea: HTMLTextAreaElement = this.textareaElement.nativeElement;
        const value: string = this.formGroup.controls.message.value || '';
        const cursorPosition: number = textarea.selectionStart || 0;
        
        const newValue: string = value.substring(0, cursorPosition) + '@' + value.substring(cursorPosition);
        this.formGroup.controls.message.setValue(newValue);
        
        const newCursorPos: number = cursorPosition + 1;
        
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(newCursorPos, newCursorPos);
            this.onInput();
        });
    }

    protected checkMention(): void {
        if (!this.allowMentions()) return;
        
        const atIndex: number = this.findMentionStartIndex();

        if (atIndex === -1) {
            this.showUserPicker.set(false);
            this.selectedMentionIndex.set(-1);
            return;
        }

        const query: string = (this.formGroup.controls.message.value || '').substring(atIndex + 1, this.textareaElement.nativeElement.selectionStart);
        if (this.mentionQuery() !== query) {
            this.mentionQuery.set(query);
            this.selectedMentionIndex.set(-1);
        }

        const hasUsers: boolean = this.filteredUsers().length > 0;
        const hasGroups: boolean = this.filteredStores().length > 0;
        const currentTab: number = this.selectedTabIndex();

        if (this.allStores().length > 0) {
            if (currentTab === 0 && !hasUsers && hasGroups) {
                this.selectedTabIndex.set(1);
            } else if (currentTab === 1 && !hasGroups && hasUsers) {
                this.selectedTabIndex.set(0);
            }
        }

        this.showUserPicker.set(hasUsers || hasGroups);
    }

    protected selectedTabChanged(index: number): void {
        this.selectedTabIndex.set(index);
        this.selectedMentionIndex.set(-1);
        this.expandedStoreId.set(null);
    }

    protected toggleStoreExpand(storeId: number): void {
        this.expandedStoreId.set(this.expandedStoreId() === storeId ? null : storeId);
    }

    protected onInput(): void {
        this.setTextareaHeight();
        this.checkMention();
    }

    protected onFileSelected(event: any): void {
        if (!this.allowAttachments()) return;
        const files: FileList = event.target.files;
        this.processFiles(files);
        this.fileInput.nativeElement.value = '';
    }

    protected onDragOver(event: DragEvent): void {
        if (!this.allowAttachments()) return;
        event.preventDefault();
        event.stopPropagation();
        this.isDragging.set(true);
    }

    protected onDragLeave(event: DragEvent): void {
        if (!this.allowAttachments()) return;
        event.preventDefault();
        event.stopPropagation();
        this.isDragging.set(false);
    }

    protected onDrop(event: DragEvent): void {
        if (!this.allowAttachments()) return;
        event.preventDefault();
        event.stopPropagation();
        this.isDragging.set(false);

        const files = event.dataTransfer?.files;
        if (files) {
            this.processFiles(files);
        }
    }

    protected onKeyDown(event: KeyboardEvent): void {
        const pickerOpen: boolean = this.allowMentions() && this.showUserPicker();
        const activeList = this.selectedTabIndex() === 0 ? this.filteredUsers() : this.filteredStores();
        const hasItems = pickerOpen && !!activeList.length;

        if (event.key === 'Enter') {
            if (hasItems) {
                event.preventDefault();
                const selectedItem = activeList[this.selectedMentionIndex()];
                if (selectedItem) {
                    if (this.selectedTabIndex() === 0) {
                        this.selectUser(selectedItem as IMentionableUsers);
                    } else {
                        this.selectStore(selectedItem as StoreItem);
                    }
                }
            } else if (!event.shiftKey) {
                event.preventDefault();
                this.sendMessage();
            }
            return;
        }

        if (hasItems) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                const nextIndex = Math.min(this.selectedMentionIndex() + 1, activeList.length - 1);
                this.selectedMentionIndex.set(nextIndex);
                this.scrollSelectedIntoView();
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                const prevIndex = Math.max(this.selectedMentionIndex() - 1, 0);
                this.selectedMentionIndex.set(prevIndex);
                this.scrollSelectedIntoView();
            } else if (event.key === 'Tab') {
                event.preventDefault();
                const selectedItem = activeList[this.selectedMentionIndex()];
                if (selectedItem) {
                    if (this.selectedTabIndex() === 0) {
                        this.selectUser(selectedItem as IMentionableUsers);
                    } else {
                        this.selectStore(selectedItem as StoreItem);
                    }
                }
            } else if (event.key === 'Escape') {
                event.preventDefault();
                this.showUserPicker.set(false);
                this.selectedMentionIndex.set(-1);
                event.stopPropagation();
            }
        }
    }

    protected onKeyUp(event: KeyboardEvent): void {
        if (event.key === 'Escape') return;
        this.checkMention();
    }

    protected onPaste(event: ClipboardEvent): void {
        if (!this.allowAttachments()) return;
        const items = event.clipboardData?.items;
        if (items) {
            const files: Array<File> = [];
            for (let i = 0; i < items.length; i++) {
                if (items[i].kind === 'file') {
                    const file = items[i].getAsFile();
                    if (file) files.push(file);
                }
            }
            if (files.length > 0) {
                event.preventDefault();
                this.processFiles(files);
            }
        }
    }

    protected openImage(file: AttachmentFile): void {
        if (file.loading) return;
        const files: Array<AttachmentFile> = this.formGroup.controls.files.value.filter((file: AttachmentFile) => file.previewUrl && !file.loading);

        this.dialog
            .open<ImagesViewModalComponent, IImagesViewModalData>(ImagesViewModalComponent, {
                width: 'auto',
                autoFocus: false,
                backdropClass: 'back-drop',
                data: {
                    images: files.map((file: AttachmentFile) => file.previewUrl as string),
                    types: files.map((file: AttachmentFile) => file.file.type.startsWith('video/') ? 'video' : 'image'),
                    names: files.map((file: AttachmentFile) => file.file.name),
                    index: files.indexOf(file),
                    showMainButton: false,
                    showSecondaryButton: false
                }
            })
            .afterClosed().subscribe();
    }

    protected removeFile(index: number): void {
        const updated: Array<AttachmentFile> = [...this.formGroup.controls.files.value];
        const [removed] = updated.splice(index, 1);
        if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
        if (removed?.loading && removed.subscription) removed.subscription.unsubscribe();
        if (removed?.key) this.chatService.deleteFile(removed.key).subscribe();
        this.formGroup.controls.files.setValue(updated);
    }

    protected scrollToReplyMessage(): void {
        this.chatService.scrollToMessage$.next(this.repliedMessage().id as number);
    }

    protected selectUser(user: IMentionableUsers): void {
        if (!this.allowMentions()) return;

        const value: string = this.formGroup.controls.message.value || '';
        const cursorPosition: number = this.textareaElement.nativeElement.selectionStart || 0;
        const textAfterCursor: string = value.substring(cursorPosition);
        const atIndex: number = this.findMentionStartIndex();
        
        if (atIndex === -1) return;

        const textBeforeAt: string = value.substring(0, atIndex);
        const newTextBeforeCursor: string = textBeforeAt + `@${user.full_name.trim()} `;
        
        this.formGroup.controls.message.setValue(newTextBeforeCursor + textAfterCursor);
        this.mentionedUsersIds.add(user.id);
        this.showUserPicker.set(false);
        this.selectedMentionIndex.set(-1);
        
        setTimeout(() => {
            const newPos: number = newTextBeforeCursor.length;
            this.textareaElement.nativeElement.focus();
            this.textareaElement.nativeElement.setSelectionRange(newPos, newPos);
            this.setTextareaHeight();
        });
    }

    protected selectStore(store: StoreItem): void {
        if (!this.allowMentions()) return;

        const value: string = this.formGroup.controls.message.value || '';
        const cursorPosition: number = this.textareaElement.nativeElement.selectionStart || 0;
        const textAfterCursor: string = value.substring(cursorPosition);
        const atIndex: number = this.findMentionStartIndex();
        
        if (atIndex === -1) return;

        const usersInStore: Array<IMentionableUsers> = store.users;
        if (usersInStore.length === 0) return;

        const mentionsString = usersInStore.map(u => `@${u.full_name.trim()}`).join(' ') + ' ';
        const textBeforeAt = value.substring(0, atIndex);
        const newTextBeforeCursor = textBeforeAt + mentionsString;
        
        this.formGroup.controls.message.setValue(newTextBeforeCursor + textAfterCursor);
        
        usersInStore.forEach(u => this.mentionedUsersIds.add(u.id));
        
        this.showUserPicker.set(false);
        this.selectedMentionIndex.set(-1);
        
        setTimeout(() => {
            const newPos: number = newTextBeforeCursor.length;
            this.textareaElement.nativeElement.focus();
            this.textareaElement.nativeElement.setSelectionRange(newPos, newPos);
            this.setTextareaHeight();
        });
    }

    protected cancelReply(): void {
        this.repliedMessage.set(null);
    }

    protected async sendMessage(): Promise<void> {
        if (this.isSendDisabled()) return;

        const hasContent: boolean = !!this.formGroup.controls.message.value?.trim();
        let hasUploadedFiles: boolean = false;
        let uploadedFiles: Array<AttachmentFile> = [];
        
        if (this.allowAttachments()) {
            const files = this.formGroup.controls.files.value;
            uploadedFiles = files.filter(f => !f.loading && f.key);
            hasUploadedFiles = uploadedFiles.length > 0;

            if (files?.length) {
                files.forEach(f => {
                    if (f.loading && f.subscription) f.subscription.unsubscribe();
                });
            }
        }

        if (!hasContent && !hasUploadedFiles) return;

        let content: string = this.formGroup.controls.message.value?.trim() || '';
        const message: INewChatMessage = { content };

        if (this.repliedMessage()) {
            message.reply_to = this.repliedMessage()?.id as number;
            message.localData = {
                replyContent: this.repliedMessage()?.content,
                replyUser: this.repliedMessage()?.user
            };
        }

        if (this.allowMentions() && content) {
            const users = this.chatService.mentionableUsers();
            if (users.length) {
                const sortedUsers = [...users].sort((a, b) => b.full_name.length - a.full_name.length);
                const escapedNames = sortedUsers.map(u => u.full_name.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
                
                const regex = new RegExp(`@(${escapedNames.join('|')})(?![a-zA-Z0-9])`, 'g');
                const finalMentionedIds: Set<number> = new Set();
                
                content = content.replace(regex, (match, name, offset, fullString) => {
                    const before = fullString.substring(0, offset);
                    const after = fullString.substring(offset + match.length);
                    
                    if (before.endsWith('[') && after.startsWith('](mention:')) {
                        const idMatch = after.match(/^\]\(mention:(\d+)\)/);
                        if (idMatch) finalMentionedIds.add(Number(idMatch[1]));
                        return match;
                    }

                    const user = sortedUsers.find(u => u.full_name.trim() === name && this.mentionedUsersIds.has(u.id));
                                 
                    if (user) {
                        finalMentionedIds.add(user.id);
                        return `[${match}](mention:${user.id})`;
                    }
                    return match;
                });
                
                message.content = content;
                message.mentioned_users = Array.from(finalMentionedIds);
            }
        }

        if (hasUploadedFiles) {
            message.attachments = uploadedFiles.map(f => ({
                key: f.key as string,
                file: f.link,
                name: f.file.name
            }));
        }
        
        // Track Message Sent event
        this.mixpanelService.trackPage(MixpanelEvents.MessageSent, {
            has_attachment: hasUploadedFiles,
            attachment_type: uploadedFiles?.map(f => f.file.type).join('; ')
        });
        
        this.chatService.newMessage$.next(message);
        this.formGroup.controls.message.setValue('');
        if (this.allowAttachments()) {
            this.formGroup.controls.files.value?.forEach(f => {
                if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
            });
            this.formGroup.controls.files.setValue([]);
        }
        this.mentionedUsersIds.clear();
        this.showUserPicker.set(false);
        this.repliedMessage.set(null);
        this.chatService.tempMessage = null;
        this.selectedMentionIndex.set(-1);
        this.textareaElement.nativeElement.focus();
        this.setTextareaHeight();
	}
}